import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import api from '../../utils/api';
import { toast } from 'sonner';

const Assignments = () => {
  const [operators, setOperators] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedRole, setSelectedRole] = useState('Staff');
  const [loadingOperators, setLoadingOperators] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const loadOperators = async () => {
    setLoadingOperators(true);
    try {
      const res = await api.get('/ops/operators');
      setOperators(res.data || []);
    } catch (error) {
      toast.error('Error al cargar operadores');
    } finally {
      setLoadingOperators(false);
    }
  };

  const loadTenants = async () => {
    try {
      const res = await api.get('/tenants');
      setTenants(res.data || []);
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
  };

  const loadAssignments = async (operatorId) => {
    if (!operatorId) return;
    setLoadingAssignments(true);
    try {
      const res = await api.get(`/ops/operators/${operatorId}/assignments`);
      setAssignments(res.data || []);
    } catch (error) {
      toast.error('Error al cargar asignaciones');
    } finally {
      setLoadingAssignments(false);
    }
  };

  useEffect(() => {
    loadOperators();
    loadTenants();
  }, []);

  useEffect(() => {
    if (selectedOperator?.id) {
      loadAssignments(selectedOperator.id);
    } else {
      setAssignments([]);
    }
  }, [selectedOperator]);

  const filteredOperators = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return operators.filter((operator) => {
      if (!normalized) return true;
      return (
        operator.full_name?.toLowerCase().includes(normalized) ||
        operator.email?.toLowerCase().includes(normalized)
      );
    });
  }, [operators, query]);

  const handleAssign = async () => {
    if (!selectedOperator?.id || !selectedTenant) {
      toast.error('Selecciona operador y cliente');
      return;
    }
    try {
      await api.post(`/ops/operators/${selectedOperator.id}/assign`, {
        tenant_id: selectedTenant,
        role: selectedRole,
      });
      toast.success('Asignacion creada');
      loadAssignments(selectedOperator.id);
      loadOperators();
    } catch (error) {
      toast.error('Error al asignar operador');
    }
  };

  const handleUnassign = async (tenantId) => {
    if (!selectedOperator?.id) return;
    try {
      await api.delete(`/ops/operators/${selectedOperator.id}/assign/${tenantId}`);
      toast.success('Asignacion eliminada');
      loadAssignments(selectedOperator.id);
      loadOperators();
    } catch (error) {
      toast.error('Error al eliminar asignacion');
    }
  };

  const availableTenants = tenants.filter(
    (tenant) => !assignments.some((assignment) => assignment.tenant_id === tenant.id)
  );

  return (
    <div data-testid="global-admin-assignments" className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Asignaciones de operadores</h1>
          <p className="text-muted-foreground">Asigna clientes a operadores y gestiona el acceso</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardContent className="p-4 space-y-3">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar operador"
              data-testid="operator-search"
            />
            {loadingOperators ? (
              <p className="text-sm text-muted-foreground">Cargando operadores...</p>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {filteredOperators.map((operator) => {
                  const isActive = selectedOperator?.id === operator.id;
                  return (
                    <button
                      key={operator.id}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                        isActive ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/40'
                      }`}
                      onClick={() => setSelectedOperator(operator)}
                      data-testid={`operator-${operator.id}`}
                    >
                      <div className="font-medium">{operator.full_name || operator.email}</div>
                      <div className="text-xs text-muted-foreground">{operator.email}</div>
                      <div className="text-xs text-muted-foreground">
                        Clientes asignados: {operator.assignment_count || 0}
                      </div>
                    </button>
                  );
                })}
                {filteredOperators.length === 0 && (
                  <p className="text-sm text-muted-foreground">No hay operadores</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger className="min-w-[220px]" data-testid="assignment-tenant">
                  <SelectValue placeholder="Selecciona cliente" />
                </SelectTrigger>
                <SelectContent>
                  {availableTenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name || tenant.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="min-w-[180px]" data-testid="assignment-role">
                  <SelectValue placeholder="Rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Staff">Staff</SelectItem>
                  <SelectItem value="Owner">Owner</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleAssign} data-testid="assignment-add">
                Asignar
              </Button>
            </div>

            <div className="border rounded-lg">
              <div className="grid grid-cols-5 gap-2 px-4 py-2 text-xs uppercase text-muted-foreground border-b">
                <span className="col-span-2">Cliente</span>
                <span>Rol</span>
                <span>Asignado</span>
                <span className="text-right">Acciones</span>
              </div>
              {loadingAssignments ? (
                <div className="p-4 text-sm text-muted-foreground">Cargando asignaciones...</div>
              ) : assignments.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No hay asignaciones</div>
              ) : (
                assignments.map((assignment) => {
                  const tenant = tenants.find((t) => t.id === assignment.tenant_id);
                  return (
                    <div
                      key={`${assignment.tenant_id}-${assignment.role}`}
                      className="grid grid-cols-5 gap-2 px-4 py-3 text-sm border-b last:border-b-0 items-center"
                    >
                      <span className="col-span-2 font-medium">{tenant?.name || assignment.tenant_id}</span>
                      <span>{assignment.role}</span>
                      <span className="text-muted-foreground">
                        {assignment.assigned_at?.split('T')[0] || '-'}
                      </span>
                      <div className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleUnassign(assignment.tenant_id)}
                          data-testid={`assignment-remove-${assignment.tenant_id}`}
                        >
                          Quitar
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Assignments;
